/**
 * Rule-aware derivations over per-period tallies: team fouls / penalty and timeouts remaining.
 * Arrays are indexed by periodNo - 1.
 */
import type { RuleSet } from '../rules/ruleset';
import { firstHalfLastPeriod, isOvertime, teamFoulPenaltyAtFor } from '../rules/ruleset';
import type { PerSide, TeamSide } from '../types';

function at(list: readonly PerSide<number>[], periodNo: number, side: TeamSide): number {
  const row = list[periodNo - 1];
  return row === undefined ? 0 : row[side];
}

function sumRange(
  list: readonly PerSide<number>[],
  fromPeriod: number,
  toPeriod: number,
  side: TeamSide,
): number {
  let total = 0;
  for (let p = fromPeriod; p <= toPeriod; p++) total += at(list, p, side);
  return total;
}

export interface TeamFoulStatus {
  /** Fouls that count toward the penalty in this period (includes OT carry-over). */
  count: number;
  /** Threshold in force: free throws from this foul number on. */
  penaltyAt: number;
  /**
   * Team-foul penalty situation (FIBA Art. 41 wording): the team has committed
   * penaltyAt − 1 fouls, so every further foul is penalized. Drives the penalty badge.
   */
  inPenalty: boolean;
  /** Fouls the team can still commit before reaching the penalty situation. */
  foulsToGive: number;
}

/**
 * Team fouls that count in `periodNo`. With `overtimeFoulsCarryOver` (FIBA), every overtime
 * counts together with the last regulation period, cumulatively across all overtimes.
 */
export function countedTeamFouls(
  rules: RuleSet,
  fouls: readonly PerSide<number>[],
  periodNo: number,
  side: TeamSide,
): number {
  if (periodNo < 1) return 0;
  if (isOvertime(rules, periodNo) && rules.overtimeFoulsCarryOver) {
    return sumRange(fouls, rules.periods, periodNo, side);
  }
  return at(fouls, periodNo, side);
}

export function teamFoulStatus(
  rules: RuleSet,
  fouls: readonly PerSide<number>[],
  periodNo: number,
  side: TeamSide,
): TeamFoulStatus {
  const count = countedTeamFouls(rules, fouls, periodNo, side);
  const penaltyAt = teamFoulPenaltyAtFor(rules, Math.max(1, periodNo));
  return {
    count,
    penaltyAt,
    inPenalty: count >= penaltyAt - 1,
    foulsToGive: Math.max(0, penaltyAt - 1 - count),
  };
}

/** Timeouts the team may take in `periodNo` (period 0 = what it will have in period 1). */
export function timeoutsAllowed(rules: RuleSet, periodNo: number): number {
  const t = rules.timeouts;
  if (isOvertime(rules, periodNo)) return t.perOvertime;
  switch (t.kind) {
    case 'perHalf':
      return periodNo <= firstHalfLastPeriod(rules) ? t.firstHalf : t.secondHalf;
    case 'perGame':
      return t.perGame;
    case 'perPeriod':
      return t.perPeriod;
  }
}

/** Timeouts already used that count against the allowance of `periodNo`. */
export function timeoutsUsedFor(
  rules: RuleSet,
  used: readonly PerSide<number>[],
  periodNo: number,
  side: TeamSide,
): number {
  if (periodNo < 1) return 0;
  if (isOvertime(rules, periodNo)) return at(used, periodNo, side);
  const t = rules.timeouts;
  switch (t.kind) {
    case 'perHalf': {
      const half = firstHalfLastPeriod(rules);
      return periodNo <= half
        ? sumRange(used, 1, half, side)
        : sumRange(used, half + 1, rules.periods, side);
    }
    case 'perGame':
      return sumRange(used, 1, rules.periods, side);
    case 'perPeriod':
      return at(used, periodNo, side);
  }
}

export function timeoutsRemaining(
  rules: RuleSet,
  used: readonly PerSide<number>[],
  periodNo: number,
  side: TeamSide,
): number {
  const p = Math.max(1, periodNo);
  return Math.max(0, timeoutsAllowed(rules, p) - timeoutsUsedFor(rules, used, p, side));
}
